@if (!usagesReported()) {
  <p
    data-testid="occupation-unreported"
    class="border-default text-text-secondary rounded-sm border border-dashed px-2 py-1 text-[11px]">
    <i [class]="ICONS.warningCircle" class="mr-1"></i>
    {{ 'visualization.occupation.unreported' | translate }}
  </p>
}

@for (panel of areaPanels(); track panelKey(panel.series)) {
  <div class="flex min-w-0 flex-col gap-1.5">
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-text-primary truncate text-xs font-semibold">
        {{ panel.series.infrastructureName }}
      </span>
      <span class="text-text-secondary shrink-0 font-mono text-[11px] tabular-nums">
        {{ panel.series.peak
        }}{{ panel.series.capacity === null ? '' : '/' + panel.series.capacity }}
      </span>
    </div>

    <!-- Stated before the chart rather than under it: it says the panel is not in the form
         the control asks for, which the reader needs before reading the marks, not after. -->
    @if (panel.degraded) {
      <p data-testid="ladder-degraded" class="text-text-tertiary text-[11px]">
        {{ 'visualization.occupation.ladder_unavailable.not_slots' | translate }}
      </p>
    }

    <!-- The tooltip is positioned as a percentage of this box, so it wraps the plot alone:
         the panel around it also holds a header and a note, whose heights vary. -->
    <div class="relative">
      <!-- Cleared once, on leaving the chart, rather than per step: the hit columns tile the
           plot, so a per-step mouseleave fires on every crossing and blanks the tooltip for a
           frame in the middle of a sweep. -->
      <svg
        role="img"
        [attr.aria-label]="panel.label"
        [attr.viewBox]="'0 0 ' + panel.geometry.width + ' ' + panel.geometry.height"
        [style.max-width.px]="panel.geometry.width"
        class="block h-auto w-full"
        (mouseleave)="hovered.set(null)">
        @for (tick of panel.ticks; track tick.x) {
          <line
            [attr.x1]="tick.x"
            [attr.x2]="tick.x"
            [attr.y1]="panel.geometry.padTop"
            [attr.y2]="panel.baselineY"
            stroke="var(--border-weak)"
            stroke-width="1" />
          <text
            [attr.x]="tick.x"
            [attr.y]="panel.geometry.height - 3"
            text-anchor="middle"
            fill="var(--text-tertiary)"
            class="font-mono text-[8px]">
            {{ tick.label }}
          </text>
        }

        <line
          [attr.x1]="panel.geometry.padLeft"
          [attr.x2]="panel.geometry.width - 6"
          [attr.y1]="panel.baselineY"
          [attr.y2]="panel.baselineY"
          stroke="var(--border-default)"
          stroke-width="1" />

        @for (label of panel.yLabels; track label.label) {
          <text
            [attr.x]="panel.geometry.padLeft - 5"
            [attr.y]="label.y + 3"
            text-anchor="end"
            fill="var(--text-tertiary)"
            class="font-mono text-[8px]">
            {{ label.label }}
          </text>
        }

        @if (panel.areaPath) {
          <path [attr.d]="panel.areaPath" [attr.fill]="'var(--occupation-usage-wash)'" />
        }

        @for (breach of panel.breaches; track breach.x) {
          <rect
            data-testid="breach-band"
            [attr.x]="breach.x"
            [attr.y]="breach.y"
            [attr.width]="breach.width"
            [attr.height]="breach.height"
            fill="var(--occupation-breach-wash)" />
        }

        @if (panel.capacityY !== null) {
          <line
            data-testid="capacity-rule"
            [attr.x1]="panel.geometry.padLeft"
            [attr.x2]="panel.geometry.width - 6"
            [attr.y1]="panel.capacityY"
            [attr.y2]="panel.capacityY"
            stroke="var(--occupation-capacity)"
            stroke-width="1.5"
            stroke-dasharray="4 3"
            stroke-linecap="round" />
        }

        <path
          [attr.d]="panel.usagePath"
          fill="none"
          stroke="var(--occupation-usage)"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round" />

        @if (markRect(panel.geometry); as mark) {
          <rect
            data-testid="mark-window"
            [attr.x]="mark.x"
            [attr.y]="mark.y"
            [attr.width]="mark.width"
            [attr.height]="mark.height"
            fill="none"
            stroke="var(--text-primary)"
            stroke-width="1"
            stroke-dasharray="3 2"
            opacity="0.55"
            pointer-events="none" />
        }

        @for (hit of panel.hits; track hit.x) {
          <rect
            data-testid="step-hit"
            [attr.x]="hit.x"
            [attr.y]="panel.geometry.padTop"
            [attr.width]="hit.width"
            [attr.height]="hit.height"
            fill="transparent"
            class="cursor-crosshair"
            (mouseenter)="
              hovered.set({
                panelKey: panelKey(panel.series),
                step: hit.step,
                capacity: panel.series.capacity,
                reference: $any($event.target)
              })
            " />
        }
      </svg>

      @if (hoveredIn(panel); as h) {
        <app-occupation-tooltip
          #areaTooltip
          class="fixed top-0 left-0 z-50"
          [step]="h.step"
          [capacity]="h.capacity" />
      }
    </div>
  </div>
}

@for (panel of ladderPanels(); track panelKey(panel.series)) {
  <div data-testid="ladder" class="flex min-w-0 flex-col gap-1.5">
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-text-primary truncate text-xs font-semibold">
        {{ panel.series.infrastructureName }}
      </span>
      <span class="text-text-secondary shrink-0 font-mono text-[11px] tabular-nums">
        {{ panel.series.lanes.length }}/{{ panel.series.capacity }}
      </span>
    </div>

    <div class="relative">
      <svg
        role="img"
        [attr.aria-label]="panel.label"
        [attr.viewBox]="'0 0 ' + panel.geometry.width + ' ' + panel.geometry.height"
        [style.max-width.px]="panel.geometry.width"
        class="block h-auto w-full">
        @for (lane of panel.lanes; track lane.index) {
          <rect
            data-testid="ladder-lane"
            [attr.data-overflow]="lane.overflow"
            [attr.x]="panel.geometry.padLeft"
            [attr.y]="lane.y"
            [attr.width]="panel.geometry.width - panel.geometry.padLeft - 6"
            [attr.height]="lane.height"
            rx="1"
            [attr.fill]="
              lane.overflow ? 'var(--occupation-lane-over)' : 'var(--occupation-lane-empty)'
            " />

          @for (block of lane.blocks; track block.stopoverUid + block.x) {
            <rect
              [attr.data-route]="block.routeUid"
              [attr.x]="block.x"
              [attr.y]="lane.y"
              [attr.width]="block.width"
              [attr.height]="lane.height"
              rx="2"
              class="occupation-block"
              [class.is-dimmed]="
                hoveredRoute() !== null && hoveredRoute() !== block.routeUid
              "
              [attr.fill]="
                block.overflow ? 'var(--occupation-breach)' : 'var(--occupation-usage)'
              "
              (mouseenter)="
                hoveredRoute.set(block.routeUid);
                hoveredBlock.set({
                  panelKey: panelKey(panel.series),
                  block,
                  reference: $any($event.target)
                })
              "
              (mouseleave)="hoveredRoute.set(null); hoveredBlock.set(null)" />
            @if (block.label) {
              <text
                [attr.x]="block.x + 6"
                [attr.y]="lane.y + lane.height / 2 + 3"
                fill="var(--bg-surface)"
                pointer-events="none"
                class="font-mono text-[9px]">
                {{ block.label }}
              </text>
            }
          }
        }

        @if (panel.capacityLineY !== null) {
          <line
            [attr.x1]="panel.geometry.padLeft - 2"
            [attr.x2]="panel.geometry.width - 6"
            [attr.y1]="panel.capacityLineY"
            [attr.y2]="panel.capacityLineY"
            stroke="var(--occupation-breach)"
            stroke-width="1.5"
            stroke-dasharray="4 3" />
        }

        @for (tick of panel.ticks; track tick.x) {
          <text
            [attr.x]="tick.x"
            [attr.y]="panel.geometry.height - 3"
            text-anchor="middle"
            fill="var(--text-tertiary)"
            class="font-mono text-[8px]">
            {{ tick.label }}
          </text>
        }
      </svg>

      @if (hoveredBlockIn(panel); as block) {
        <app-occupation-block-tooltip
          #blockTooltip
          class="fixed top-0 left-0 z-50"
          [claim]="block.claim"
          [laneIndex]="block.laneIndex"
          [overflow]="block.overflow" />
      }
    </div>
  </div>
}

@for (series of unavailablePanels(); track panelKey(series)) {
  <div data-testid="ladder-unavailable" class="flex min-w-0 flex-col gap-1.5">
    <div class="flex items-baseline justify-between gap-2">
      <span class="text-text-primary truncate text-xs font-semibold">
        {{ series.infrastructureName }}
      </span>
      <span class="text-text-secondary shrink-0 font-mono text-[11px] tabular-nums">
        {{ series.peak }}
      </span>
    </div>
    <p
      class="border-default text-text-secondary rounded-sm border border-dashed px-2 py-1.5 text-[11px] leading-snug">
      {{ 'visualization.occupation.ladder_unavailable.no_capacity' | translate }}
    </p>
  </div>
}
